import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient } from './supabase';

type ClientOption = { supabase?: SupabaseClient };

export type AISettingsRec = {
  id: string;
  ai_enabled: boolean | null;
  provider: string | null;
  model: string | null;
  flags: Record<string, any> | null;
  updated_at?: string | null;
  updated_by?: string | null;
};

export type AIRuntimeSettings = Pick<AISettingsRec, 'ai_enabled' | 'model'> & {
  flags: Record<string, boolean>;
};

export async function fetchAiRuntimeSettings(
  options: ClientOption = {},
): Promise<{ data: AIRuntimeSettings | null; error: PostgrestError | null }> {
  const supabase = options.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('get_ai_runtime_settings');
  return { data: (data as AIRuntimeSettings | null) ?? null, error: error ?? null };
}

export async function fetchAdminAiSettings(
  options: ClientOption = {},
): Promise<{ data: AISettingsRec | null; error: PostgrestError | null }> {
  const supabase = options.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.from('ai_settings').select('*').limit(1).maybeSingle();
  return { data: (data as AISettingsRec | null) ?? null, error: error ?? null };
}

export async function saveAiSettings(params: {
  patch: Partial<AISettingsRec>;
  currentId?: string | null;
  supabase?: SupabaseClient;
}): Promise<{ data: AISettingsRec | null; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const targetId = params.currentId ?? (params.patch as any)?.id ?? null;

  if (targetId) {
    const { data, error } = await supabase
      .from('ai_settings')
      .update(params.patch)
      .eq('id', targetId)
      .select('*')
      .single();
    return { data: (data as AISettingsRec | null) ?? null, error: error ?? null };
  }

  const { data, error } = await supabase.from('ai_settings').insert(params.patch).select('*').single();
  return { data: (data as AISettingsRec | null) ?? null, error: error ?? null };
}

/** One recorded change to ai_settings (docs/ai-settings-change-history.md). Flags appear as `flags.<key>`. */
export type AiSettingsChange = {
  id: number;
  action: 'INSERT' | 'UPDATE' | 'DELETE';
  changes: { [field: string]: { from: unknown; to: unknown } };
  /** The auth user; null for a change made outside the Portal. */
  changed_by: string | null;
  /** The request's role (`authenticated`, `service_role`), or the database login. */
  changed_via: string;
  changed_at: string;
};

export const AI_SETTINGS_CHANGES_QUERY_KEY = ['ai-settings-changes'] as const;

/** The newest changes first. Readable by those who can read AI Controls; others get none. */
export async function fetchAiSettingsChanges(
  limit = 25,
  options: ClientOption = {},
): Promise<{ data: AiSettingsChange[]; error: PostgrestError | null }> {
  const supabase = options.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('ai_settings_changes')
    .select('id,action,changes,changed_by,changed_via,changed_at')
    .order('changed_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(limit);
  return { data: Array.isArray(data) ? (data as AiSettingsChange[]) : [], error: error ?? null };
}
